// Using literal event name to avoid bundling requirements
const CART_UPDATE_EVENT = 'cart:update';

// Import section renderer for Section Rendering API
let sectionRenderer;
try {
  const module = await import('./section-renderer.js');
  sectionRenderer = module.sectionRenderer;
} catch (e) {
  console.warn('section-renderer not available', e);
}

async function fetchCart() {
  const res = await fetch('/cart.js');
  if (!res.ok) return null;
  return res.json();
}

async function fetchRecommendationCards(productId, limit, intent) {
  if (!sectionRenderer) return [];

  try {
    // Define a consistent section ID for the API call
    let sectionId = 'cart-recommendations-render';

    const sectionToRender = document.querySelector('.cart-drawer-recommendations-render');
    if (sectionToRender && sectionToRender.id) {
      sectionId = sectionToRender.id.replace('shopify-section-', '');
    }

    // Shopify's recommendations endpoint: /recommendations/products?product_id=X&intent=Y&limit=Z
    const url = new URL(window.location.origin + '/recommendations/products');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', limit.toString());
    url.searchParams.set('intent', intent);
    url.searchParams.set('section_id', sectionId);

    // Fetch directly since we're using section_id parameter
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
    const html = await res.text();

    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Find the section wrapper by ID or class
    let section = doc.getElementById(`shopify-section-${sectionId}`);
    if (!section) {
      section = doc.querySelector('.cart-drawer-recommendations-render');
    }

    if (!section) {
      return [];
    }

    const cards = Array.from(section.querySelectorAll('.resource-list__item'));
    return cards;
  } catch (error) {
    console.error('Failed to fetch recommendation cards:', error);
    return [];
  }
}

function getSlidesRoot(root) {
  // resource-list-carousel renders slides via slideshow-slide with class resource-list__slide
  const slides = root.querySelectorAll('.resource-list__slide .resource-list__item');
  if (slides.length === 0) {
    return root.querySelectorAll('.resource-list__item');
  }
  return slides;
}

async function updateRecommendations(container) {
  const limit = Number(container.dataset.limit || 6);
  const intent = container.dataset.intent || 'related';

  let productId = null;
  // Prefer data from last cartUpdate event cached on container
  const lastItems = container.__lastCartItems;
  if (lastItems && lastItems.length) {
    productId = String(lastItems[lastItems.length - 1].product_id);
  }
  if (!productId) {
    const cart = await fetchCart();
    if (cart && cart.items && cart.items.length) {
      productId = String(cart.items[cart.items.length - 1].product_id);
    }
  }

  if (!productId) {
    return;
  }

  // Use Section Rendering API to get server-rendered product cards (includes quick add)
  const cards = await fetchRecommendationCards(productId, limit, intent);

  const slides = Array.from(getSlidesRoot(container));
  cards.slice(0, slides.length).forEach((card, idx) => {
    const slot = slides[idx];
    if (!slot) return;
    const clone = card.cloneNode(true);
    // Replace the skeleton wrapper entirely to avoid nested resource-list__item
    slot.replaceWith(clone);
  });

  // Toggle visibility if no recommendations
  if (!cards.length) {
    container.style.display = 'none';
  } else {
    container.style.display = '';
  }
}

function init(container) {
  updateRecommendations(container).catch(console.error);

  // Ensure we don't attach duplicate listeners on re-renders
  if (container.__abortController) {
    try {
      container.__abortController.abort();
    } catch {}
  }
  const ac = new AbortController();
  container.__abortController = ac;

  document.addEventListener(
    CART_UPDATE_EVENT,
    (e) => {
      const items = e?.detail?.resource?.items;
      if (Array.isArray(items)) container.__lastCartItems = items;
      updateRecommendations(container).catch(console.error);
    },
    { signal: ac.signal }
  );

  const drawer = container.closest('cart-drawer-component');
  if (drawer) {
    drawer.addEventListener(
      'cart-drawer:open',
      () => {
        updateRecommendations(container).catch(console.error);
      },
      { signal: ac.signal }
    );
  }
}

function initAll() {
  document.querySelectorAll('[data-cart-drawer-recommendations]').forEach(init);
}

function ready(fn) {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
  else fn();
}

ready(() => {
  initAll();
  // Re-init after every cart update in case the drawer section is morphed/re-rendered
  document.addEventListener(CART_UPDATE_EVENT, initAll);
});